import { useState, useEffect, useContext } from 'react';
import {
  getDataIngestionWorkflowStatus,
  WorkflowStatus,
  WorkflowTransitionResponse,
} from '../../services/workflowService';
import GlobalStateContext from '../../context/GlobalStateContext';

const useDataIngestionWorkflowStatus = () => {
  const { userDetails } = useContext(GlobalStateContext);
  const eoaId = userDetails.data?.eoaId;
  const accessToken = userDetails.data?.access_token;
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkflowStatus = async () => {
    if (!eoaId || !accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const status = await getDataIngestionWorkflowStatus(eoaId, accessToken);
      setWorkflowStatus(status);
    } catch (err) {
      console.error('Error fetching workflow status:', err);
      setError('Failed to fetch workflow status');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflowStatus();
  }, [eoaId, accessToken]);

  const updateWorkflowStatusFromTransition = (transitionResponse: WorkflowTransitionResponse) => {
    setWorkflowStatus((prevStatus) => ({
      ...prevStatus,
      current_state: transitionResponse.current_state,
      workflow_metadata: {
        ...prevStatus?.workflow_metadata,
        ...transitionResponse.workflow_metadata,
      },
      available_transitions: [], // We don't get this from transition response, so keep existing or empty
    }));
  };

  const isDataQcAvailable = () => {
    if (!workflowStatus) return false;

    const hasRejections =
      workflowStatus.workflow_metadata?.district_data_qc_rejection &&
      workflowStatus.workflow_metadata.district_data_qc_rejection.length > 0;

    const statesAvailableWithRejections = [
      'IN_PROGRESS',
      'UPLOAD_COMPLETE',
      'DISTRICT_DATA_UPLOAD_DONE',
      'EXTRACTION_COMPLETE',
    ];

    if (hasRejections && statesAvailableWithRejections.includes(workflowStatus.current_state)) {
      return true;
    }

    const unavailableStates = [
      'NOT_STARTED',
      'IN_PROGRESS',
      'UPLOAD_COMPLETE',
      'DISTRICT_DATA_UPLOAD_DONE',
      'EXTRACTION_COMPLETE',
    ];

    return !unavailableStates.includes(workflowStatus.current_state);
  };

  return {
    workflowStatus,
    isLoading,
    error,
    isDataQcAvailable: isDataQcAvailable(),
    refetch: fetchWorkflowStatus,
    updateWorkflowStatusFromTransition,
  };
};

export default useDataIngestionWorkflowStatus;
